/**
 * DOCX/PPTX API Client — calls Worker endpoints for parse/export
 *
 * Two flows:
 *   LEGACY: apiParseDocx / apiExportDocx — full zip upload, server stores in KV
 *   STATELESS: apiParseDocxStateless / apiExportDocxStateless — client unzips,
 *              server returns modified XMLs, no KV storage
 *
 * Stateless is preferred for new code (supports files >18MB, no KV TTL).
 */

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

export interface ParagraphGroupDisplay {
    sig: string;
    aiIdx?: number;
    wsText?: string;
}

export interface ParagraphDisplay {
    type: 'body' | 'textbox' | 'slide';
    slideNumber?: number;
    groups: ParagraphGroupDisplay[];
    // Smart chunker metadata (US-17-08) — DOCX only V1 (PPTX leaves these undefined).
    // Used by chunkSegmentsSmart in utils/batchChunker.ts to avoid bad cut boundaries.
    headingLevel?: number;
    inTable?: boolean;
    inDrawing?: boolean;
    pStyle?: string;
}

// ============================================
// LEGACY response (full zip → fileId in KV)
// ============================================
export interface ParseLegacyResponse {
    fileId: string;
    fileName: string;
    segments: string[];
    paragraphs: ParagraphDisplay[];
    meta: {
        bodyParas: number;
        textboxParas: number;
        chartLabels: number;
        smartArtLabels: number;
        totalSegments: number;
    };
    expiresIn: number;
}

// ============================================
// STATELESS response (filtered XMLs → no KV)
// ============================================

/**
 * Compact structure the client stores and sends back on export
 * so the server can skip the expensive re-parse step.
 * OPAQUE to client — fields differ between DOCX and PPTX; client just passes through.
 */
export type ParseStructure = Record<string, any>;

export interface ParseStatelessResponse {
    fileName: string;
    segments: string[];
    paragraphs: ParagraphDisplay[];
    chartLabels: string[];
    chartAiStart: number;
    smartLabels: string[];
    smartAiStart: number;
    meta: ParseLegacyResponse['meta'];
    parseStructure?: ParseStructure;
    warnings?: Array<{
        code: string;
        severity: 'warning' | 'info';
        message: string;
        stats?: Record<string, any>;
    }>;
}

export interface ExportStatelessResponse {
    xmls: Record<string, string>;
}

async function getAuthToken(): Promise<string | null> {
    try {
        const { supabase } = await import('../supabase/supabaseClient');
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token || null;
    } catch {
        return null;
    }
}

async function authHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
    const token = await getAuthToken();
    const h: Record<string, string> = { ...(extra || {}) };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
}

// ============================================
// LEGACY: Parse full file, store zip in KV
// ============================================

export async function apiParseDocx(file: File): Promise<ParseLegacyResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await fetch(`${WORKERS_URL}/api/parse-docx`, {
        method: 'POST',
        headers: await authHeaders(),
        body: formData,
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Parse failed (${resp.status})`);
    }
    return resp.json();
}

export async function apiParsePptx(file: File): Promise<ParseLegacyResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await fetch(`${WORKERS_URL}/api/parse-pptx`, {
        method: 'POST',
        headers: await authHeaders(),
        body: formData,
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Parse failed (${resp.status})`);
    }
    return resp.json();
}

export async function apiExportDocx(fileId: string, translated: string[]): Promise<Blob> {
    const resp = await fetch(`${WORKERS_URL}/api/export-docx`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ fileId, translated }),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Export failed (${resp.status})`);
    }
    return resp.blob();
}

export async function apiExportPptx(fileId: string, translated: string[]): Promise<Blob> {
    const resp = await fetch(`${WORKERS_URL}/api/export-pptx`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ fileId, translated }),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Export failed (${resp.status})`);
    }
    return resp.blob();
}

// ============================================
// STATELESS: Client unzips first, server handles XMLs only
// ============================================

/**
 * Parse DOCX with filtered zip (client-unzipped XMLs only).
 * No KV storage, supports files up to any size (filtered is typically 1-3MB).
 */
export async function apiParseDocxStateless(filteredZip: Blob): Promise<ParseStatelessResponse> {
    const formData = new FormData();
    formData.append('file', filteredZip, 'filtered.docx');
    const resp = await fetch(`${WORKERS_URL}/api/parse-docx`, {
        method: 'POST',
        headers: await authHeaders({ 'X-Upload-Type': 'filtered' }),
        body: formData,
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Parse failed (${resp.status})`);
    }
    return resp.json();
}

/**
 * Parse PPTX with filtered zip.
 */
export async function apiParsePptxStateless(filteredZip: Blob): Promise<ParseStatelessResponse> {
    const formData = new FormData();
    formData.append('file', filteredZip, 'filtered.pptx');
    const resp = await fetch(`${WORKERS_URL}/api/parse-pptx`, {
        method: 'POST',
        headers: await authHeaders({ 'X-Upload-Type': 'filtered' }),
        body: formData,
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Parse failed (${resp.status})`);
    }
    return resp.json();
}

export interface ExportStatelessRequest {
    xmls: Record<string, string>;
    translated: string[];
    chartLabels: string[];
    chartAiStart: number;
    smartLabels: string[];
    smartAiStart: number;
    parseStructure?: ParseStructure;
    segments?: string[];
}

/**
 * Export DOCX: server applies translations to XMLs, returns modified XMLs.
 * Client then replaces XMLs in original zip and downloads.
 */
export async function apiExportDocxStateless(body: ExportStatelessRequest): Promise<Record<string, string>> {
    const resp = await fetch(`${WORKERS_URL}/api/export-docx`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json', 'X-Upload-Type': 'xmls' }),
        body: JSON.stringify(body),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Export failed (${resp.status})`);
    }
    const { xmls } = (await resp.json()) as ExportStatelessResponse;
    return xmls;
}

export async function apiExportPptxStateless(body: ExportStatelessRequest): Promise<Record<string, string>> {
    const xmlBytes = Object.values(body.xmls || {}).reduce((sum, xml) => sum + new Blob([xml]).size, 0);
    console.log('[ExportPPTX] stateless payload', {
        xmlFiles: Object.keys(body.xmls || {}).length,
        xmlBytes,
        translated: body.translated?.length || 0,
        hasParseStructure: !!body.parseStructure,
    });
    const resp = await fetch(`${WORKERS_URL}/api/export-pptx`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json', 'X-Upload-Type': 'xmls' }),
        body: JSON.stringify(body),
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error((err as any).error || `Export failed (${resp.status})`);
    }
    const { xmls } = (await resp.json()) as ExportStatelessResponse;
    return xmls;
}
